import { computed, inject, Injectable, signal } from '@angular/core';
import { take } from 'rxjs';
import { CoreGradeScaleService } from '@lb/api/core/services/core-grade-scale.service';
import { ICoreGradeScale } from '@lb/api/core/interfaces';

/**
 * Session-cached grade scales, each with its grades already hydrated, keyed for lookup by scale ID.
 *
 * Holds EVERY scale, active or not. Grade names have to keep resolving for work already recorded
 * against a scale that has since been retired: a grading template pins the scale it was built on,
 * and deactivating that scale must not blank the final grades on enrollments using it. Anything
 * asking a user to *choose* a scale wants CoreGradeScaleService.getActiveGradeScales() instead —
 * this cache is for resolution, not for offering a choice.
 *
 * Backed by the core `grade-scales` read, which is cached server-side for 30 minutes.
 */
@Injectable({
	providedIn: 'root'
})
export class GradeStateService {
	private _gradeScaleService: CoreGradeScaleService = inject(CoreGradeScaleService);
	private _loading = false;

	public loaded = signal<boolean>(false);
	public gradeScales = signal<ICoreGradeScale[]>([]);

	/**
	 * Grades on the scale flagged as the org default, or null when no scale is flagged.
	 *
	 * Deliberately no fallback to "whatever scale came back first": a missing default is a
	 * configuration gap, and silently grading against an arbitrary scale would write GradeIDs from a
	 * scale the enrollment does not use. Transcript generation fails outright in the same situation
	 * (`transcript_no_default_grade_scale`); callers here should surface the gap rather than guess.
	 */
	public defaultGrades = computed(() => {
		const defaultGradeScale = this.gradeScales().find((x) => x.isDefault);
		return defaultGradeScale ? defaultGradeScale.grades : null;
	});

	/** One scale by ID, whether or not it is still active — see the class note on why inactive counts. */
	getGradeScaleByID(gradeScaleID: number): ICoreGradeScale | null {
		return this.gradeScales().find((x) => x.gradeScaleID === gradeScaleID) || null;
	}

	loadGradeScales(): void {
		// `loaded` means data is available; `_loading` dedupes the burst of calls a card list makes
		// while the first request is still in flight. Primed at app bootstrap, but every consumer
		// still calls this on construction so a failed load retries on the next page visit.
		if (this.loaded() || this._loading) return;
		this._loading = true;

		this._gradeScaleService
			.getAllGradeScales()
			.pipe(take(1))
			.subscribe({
				next: (gradeScales) => {
					// Not filtered to active: retired scales still have to resolve for grades already
					// recorded against them.
					this.gradeScales.set(gradeScales);
					this.loaded.set(true);
					this._loading = false;
				},
				error: () => {
					// Allow retry on failure.
					this._loading = false;
				}
			});
	}
}
